import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useLang } from '../i18n'

export function ActivatePage() {
  const { t } = useLang()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const usernameValid = /^[a-zA-Z0-9_]{3,32}$/.test(username)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!usernameValid) {
      setError(t('activateUsernameInvalid'))
      return
    }
    if (!password) {
      setError(t('errorPasswordEmpty'))
      return
    }

    setLoading(true)
    try {
      const r = await fetch('/api/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, username: username.trim(), password }),
      })
      if (r.ok) {
        navigate('/login?activated=1', { replace: true })
      } else {
        const body = await r.json().catch(() => ({}))
        setError(body?.error?.message ?? t('activateError'))
      }
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div style={{ textAlign: 'center', marginTop: '48px' }}>
        <p style={{ color: 'var(--danger)' }}>{t('activateTokenMissing')}</p>
        <Link to="/login" style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{t('backToLogin')}</Link>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{
        width: '360px', padding: '32px', border: '1px solid var(--border)',
        borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', background: 'var(--bg-surface)',
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: 'var(--text-primary)', textAlign: 'center' }}>
          {t('activateTitle')}
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {t('activateSubtitle')}
        </p>

        {error && (
          <div style={{
            padding: '10px 12px', marginBottom: '16px', background: 'var(--danger-bg)',
            border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{t('activateUsername')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('activateUsernamePlaceholder')}
              required
              autoFocus
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px' }}
            />
            {username.length > 0 && !usernameValid && (
              <span style={{ fontSize: '11px', color: 'var(--warning)' }}>{t('activateUsernameHint')}</span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{t('loginPassword')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('loginPassword')}
              required
              style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px', background: loading ? 'var(--text-muted)' : 'var(--accent)',
              color: 'var(--accent-contrast)', border: 'none', borderRadius: '4px', fontSize: '14px',
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('loading') : t('activateSubmit')}
          </button>

          <div style={{ textAlign: 'center' }}>
            <Link to="/login" style={{ fontSize: '13px', color: 'var(--text-primary)', textDecoration: 'underline' }}>
              {t('backToLogin')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
